/**
 * Visual Config — Field config helpers.
 *
 * Conversion between server-side field mappings and the ChartFieldConfig
 * format used by the visual editor's field mapping UI.
 */
import type { AggregationType, ChartFieldConfig, ChartTypeOption, TableColumn, TableSortEntry } from './types';
import type { DatasetViewColumn } from '../../../api/types';

// ── Field completeness check ───────────────────────────────────────────────

/**
 * Check whether the current field config has the minimum required fields
 * for the given chart type to produce a valid preview query.
 *
 * Returns `false` when the backend would fail or produce empty results.
 */
export function isFieldConfigComplete(
  chartType: ChartTypeOption,
  fields?: ChartFieldConfig,
): boolean {
  if (!fields) return false;
  switch (chartType) {
    case 'kpi':
      return !!fields.value?.column;
    case 'table':
      return !!fields.columns?.length && fields.columns.some((c) => !!c.column);
    case 'pie':
    case 'funnel':
      return !!fields.x?.column && !!fields.y?.[0]?.column;
    case 'scatter':
      return !!fields.x?.column && !!fields.y?.[0]?.column;
    case 'heatmap':
      return !!fields.x?.column && !!fields.yDimension?.column && !!fields.y?.[0]?.column;
    case 'histogram':
      return !!fields.x?.column; // Y is auto-count, no field needed
    default: // bar, line, area
      return !!fields.x?.column && !!fields.y?.length && fields.y.some((m) => !!m.column);
  }
}

// ── Smart auto-aggregation ─────────────────────────────────────────────────

/**
 * Pick the best default aggregation for a column based on its semantic type.
 *
 * Rules:
 *   measure   → use column's default_aggregation (fallback: sum)
 *   dimension → count_distinct
 *   id        → count_distinct
 *   time      → count
 */
export function getDefaultAggForColumn(col: DatasetViewColumn): AggregationType {
  switch (col.semantic_type) {
    case 'measure': {
      const da = col.default_aggregation;
      if (da && da !== 'none') return da as AggregationType;
      return 'sum';
    }
    case 'dimension':
      return 'count_distinct';
    case 'id':
      return 'count_distinct';
    case 'time':
      return 'count';
    default:
      return 'count';
  }
}

// ── Field config from field mappings ───────────────────────────────────────

/**
 * Convert server-side `fieldMappings` (enriched with `source_column` and
 * `aggregation`) into the `ChartFieldConfig` format used by the visual editor.
 *
 * The chart type disambiguates array-valued pie/funnel measures from the
 * dedicated KPI `value` field.
 *
 * Falls back gracefully when the enriched fields are missing (old charts).
 */
export function fieldMappingsToFieldConfig(
  fieldMappings: Record<string, unknown> | null | undefined,
  chartType: ChartTypeOption,
): ChartFieldConfig | undefined {
  if (!fieldMappings) return undefined;

  const fm = fieldMappings as Record<string, any>;
  const result: ChartFieldConfig = {};

  // x / category → dimension
  const xField = fm.x ?? fm.category;
  if (xField && typeof xField === 'object') {
    result.x = {
      column: xField.source_column ?? xField.source?.column ?? '',
      label: xField.label,
    };
  }

  // y → measures. Pie/funnel mappings may store measures as an array under
  // `value`; that shape is distinct from the KPI-only value field.
  const yFields = fm.y ?? (
    chartType !== 'kpi' && Array.isArray(fm.value) ? fm.value : null
  );
  if (Array.isArray(yFields) && yFields.length > 0) {
    result.y = yFields.map((m: any) => ({
      column: m.source_column ?? m.source?.column ?? '',
      aggregation: (m.aggregation ?? 'sum') as AggregationType,
      label: m.label,
    }));
  }

  // series / group / legend → dimension
  const seriesField = fm.series ?? fm.group ?? fm.legend;
  if (seriesField && typeof seriesField === 'object') {
    result.series = {
      column: seriesField.source_column ?? seriesField.source?.column ?? '',
      label: seriesField.label,
    };
  }

  // yDimension (heatmap Y axis — second dimension)
  const yDimField = fm.yDimension;
  if (yDimField && typeof yDimField === 'object') {
    result.yDimension = {
      column: yDimField.source_column ?? yDimField.source?.column ?? '',
      label: yDimField.label,
    };
  }

  // size (scatter bubble size)
  const sizeField = fm.size;
  if (sizeField && typeof sizeField === 'object') {
    result.size = {
      column: sizeField.source_column ?? sizeField.source?.column ?? '',
      aggregation: (sizeField.aggregation ?? 'sum') as AggregationType,
      label: sizeField.label,
    };
  }

  // KPI: value field — stored as a dedicated `value` key in field_mappings.
  // May be a single object or an array (LLM generates `value: [{...}]`).
  const rawValue = fm.value;
  const valueField = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  if (chartType === 'kpi' && valueField && typeof valueField === 'object') {
    result.value = {
      column: valueField.source_column ?? valueField.source?.column ?? '',
      aggregation: (valueField.aggregation ?? 'sum') as AggregationType,
      label: valueField.label,
    };
  }

  // Table: columns list — map missing/null aggregation → "none"
  const columnsField = fm.columns;
  if (Array.isArray(columnsField) && columnsField.length > 0) {
    result.columns = columnsField.map((c: any) => ({
      column: c.source_column ?? c.source?.column ?? c.column ?? '',
      aggregation: (c.aggregation && c.aggregation !== 'group'
        ? c.aggregation
        : 'none') as AggregationType,
      label: c.label,
    }));
  }

  // Table: sorting array
  const sortingField = fm.sorting;
  if (Array.isArray(sortingField) && sortingField.length > 0) {
    result.sorting = sortingField.map((s: any) => ({
      field: s.field ?? '',
      direction: s.direction === 'asc' ? 'asc' : 'desc',
    }));
  }

  // Return undefined if nothing was extracted
  if (!result.x && !result.y && !result.series && !result.yDimension && !result.size && !result.value && !result.columns) {
    return undefined;
  }
  return result;
}

// ── Field config → field mappings (for save) ───────────────────────────────

/**
 * Convert UI `ChartFieldConfig` back into the server-side field mappings
 * format for persistence.
 *
 * Only includes fields that have a column selected.
 * Merges into existing field mappings to preserve AI-generated metadata
 * (label, source.table, source.physical_type, semantic_type).
 */
export function fieldConfigToFieldMappings(
  fields: ChartFieldConfig | undefined,
  existing: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {
  if (!fields) return existing as Record<string, unknown> | undefined;

  const base = { ...(existing ?? {}) } as Record<string, any>;

  // x / category
  if (fields.x?.column) {
    const prev = base.x ?? base.category ?? {};
    base.x = {
      ...prev,
      source_column: fields.x.column,
      source: { ...(prev.source ?? {}), column: fields.x.column },
      label: fields.x.label ?? prev.label ?? fields.x.column,
    };
  }

  // y / value → measures
  if (fields.y && fields.y.length > 0) {
    const prevY = Array.isArray(base.y) ? base.y : [];
    base.y = fields.y.map((m, i) => {
      const prev = prevY[i] ?? {};
      return {
        ...prev,
        source_column: m.column,
        source: { ...(prev.source ?? {}), column: m.column },
        aggregation: m.aggregation,
        label: m.label ?? prev.label ?? m.column,
      };
    });
  }

  // series / breakdown
  if (fields.series?.column) {
    const prev = base.series ?? {};
    base.series = {
      ...prev,
      source_column: fields.series.column,
      source: { ...(prev.source ?? {}), column: fields.series.column },
      label: fields.series.label ?? prev.label ?? fields.series.column,
    };
  } else {
    delete base.series;
  }

  // yDimension (heatmap Y axis)
  if (fields.yDimension?.column) {
    const prev = base.yDimension ?? {};
    base.yDimension = {
      ...prev,
      source_column: fields.yDimension.column,
      source: { ...(prev.source ?? {}), column: fields.yDimension.column },
      label: fields.yDimension.label ?? prev.label ?? fields.yDimension.column,
    };
  } else {
    delete base.yDimension;
  }

  // size (scatter bubble)
  if (fields.size?.column) {
    const prev = base.size ?? {};
    base.size = {
      ...prev,
      source_column: fields.size.column,
      source: { ...(prev.source ?? {}), column: fields.size.column },
      aggregation: fields.size.aggregation,
      label: fields.size.label ?? prev.label ?? fields.size.column,
    };
  } else {
    delete base.size;
  }

  // KPI: value → stored as dedicated `value` key in field_mappings
  if (fields.value?.column) {
    const prev = base.value ?? {};
    base.value = {
      ...prev,
      source_column: fields.value.column,
      source: { ...(prev.source ?? {}), column: fields.value.column },
      aggregation: fields.value.aggregation,
      label: fields.value.label ?? prev.label ?? fields.value.column,
    };
    // Remove standard X/Y keys for KPI — they're not used
    delete base.x;
    delete base.y;
    delete base.series;
  } else {
    delete base.value;
  }

  // Table: columns list — "none" → null aggregation for backend
  if (fields.columns && fields.columns.length > 0) {
    base.columns = fields.columns.map((col: TableColumn) => ({
      source_column: col.column,
      source: { column: col.column },
      aggregation: col.aggregation === 'none' ? null : col.aggregation,
      label: col.label ?? col.column,
      column: col.column,
    }));
    // Remove standard X/Y keys for Table — they're not used
    delete base.x;
    delete base.y;
    delete base.series;
  } else {
    delete base.columns;
  }

  // Table: sorting array
  if (fields.sorting && fields.sorting.length > 0) {
    base.sorting = fields.sorting.map((s: TableSortEntry) => ({
      field: s.field,
      direction: s.direction,
    }));
  } else {
    delete base.sorting;
  }

  return base;
}
